import { AmbiguousError } from "../types/AmbiguousError";

/**
 * Converts an `AmbiguousError` to an array of `string | Error`.
 * @param ambiguousError An error that has type `string | Error | (string | Error)[]`.
 *
 * @remarks
 * Doesn't change the source of the error, just wraps in an array if it is not one.
 */

export const convertAmbiguousErrorToArray = (
  ambiguousError: AmbiguousError
): (string | Error)[] =>
  Array.isArray(ambiguousError) ? ambiguousError : [ambiguousError];

/**
 * Converts an `AmbiguousError` to `Error[]` errors.
 * @param ambiguousError An error that has type `string | Error | (string | Error)[]`.
 *
 * @remarks
 * If any of the error messages are of type `string`, they are wrapped to create the `Error`.
 */
export const normalizeAmbiguousErrors = (
  ambiguousError: AmbiguousError
): Error[] => {
  const errors = convertAmbiguousErrorToArray(ambiguousError);
  if (errors.every(error => error instanceof Error)) {
    return errors as Error[];
  }
  return errors.map(error =>
    typeof error === "string" ? new Error(error) : error
  );
};

/**
 * Converts an `AmbiguousError` to `string[]` messages.
 * @param ambiguousError An error that has type `string | Error | (string | Error)[]`.
 *
 * @remarks
 * If any of the error messages are of type `Error`, they are unwrapped to get the message.
 */
export const normalizeAmbiguousErrorMessages = (
  ambiguousError: AmbiguousError
): string[] => {
  const errors = convertAmbiguousErrorToArray(ambiguousError);
  if (errors.every(error => typeof error === "string")) {
    return errors as string[];
  }
  return errors.map(error =>
    typeof error === "string" ? error : error.message
  );
};
